import { api } from '../utils/api'

interface CashSessionApi {
  id: string
  openedAt: string
  status?: 'open' | 'closed'
  user?: string
  initialAmount?: number
}

interface MovementApi {
  id: string
  type: 'sale' | 'withdraw' | 'expense'
  amount: number
  reason?: string
  method?: string
  orderId?: string
  createdAt: string
}

function mapCashSession(session: CashSessionApi): CashSession {
  const { id, ...rest } = session
  return { id: id, ...rest }
}

function mapMovement(mov: MovementApi): Movement {
  const { id, ...rest } = mov
  return { id: id, ...rest }
}

export interface CashSession {
  id: string
  openedAt: string
  status?: 'open' | 'closed'
  user?: string
  initialAmount?: number
}

export interface CashSummary {
  initialAmount: number
  totalSales: number
  cashSales: number
  withdrawals?: number
  expenses?: number
  expectedInDrawer: number
}

export interface Movement {
  id: string
  type: 'sale' | 'withdraw' | 'expense'
  amount: number
  reason?: string
  method?: string
  orderId?: string
  createdAt: string
}

export async function getCashSession() {
  const { data } = await api.get<any>('/cash/session')
  if (!data) return null
  // API may return { open: boolean, session: {...} }
  if ('open' in data) {
    if (!data.open || !data.session) return null
    return mapCashSession(data.session as CashSessionApi)
  }
  return mapCashSession(data as CashSessionApi)
}

export async function openCashSession(payload: { user: string; initialAmount: number }) {
  const { data } = await api.post<CashSessionApi>('/cash/open', payload)
  return mapCashSession(data)
}

export async function withdrawCash(payload: { session: string; amount: number; reason: string }) {
  const { data } = await api.post('/cash/withdraw', payload)
  window.dispatchEvent(new Event('cash:updated'))
  return data
}

export async function registerSale(payload: { session: string; orderId: string; amount: number }) {
  const { data } = await api.post('/cash/sale', payload)
  window.dispatchEvent(new Event('cash:updated'))
  return data
}

export async function closeCashSession(payload: { sessionId: string; finalAmount: number; observations?: string }) {
  const { data } = await api.post('/cash/close', payload)
  window.dispatchEvent(new Event('cash:updated'))
  return data
}

export async function getMovements(sessionId: string) {
  const { data } = await api.get<MovementApi[] | { movements?: MovementApi[] }>(
    `/cash/session/${sessionId}/movements`
  )
  if (Array.isArray(data)) {
    return data.map(mapMovement)
  }
  if (data && Array.isArray(data.movements)) {
    return data.movements.map(mapMovement)
  }
  return []
}

export async function getCashSummary(sessionId: string) {
  const { data } = await api.get<CashSummary>(`/cash/session/${sessionId}/summary`)
  return data
}

export async function getAllMovements(): Promise<Movement[]> {
  const { data } = await api.get<MovementApi[]>('/cash/movements')
  return data.map(mapMovement)
}
